import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, PackageSearch, TrendingDown, Clock, AlertTriangle } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  FilterBar,
  FilterChip,
  StatusBadge,
  PageHeader,
  type ColumnDef,
} from '@/components/ui'
import { useToast } from '@/components/ui'

// ─── Types ────────────────────────────────────────────────────────────────────

interface InventoryTurnoverRow {
  articleId: string
  itemCode: string
  itemName: string
  groupName: string | null
  stockQuantity: number
  totalOutbound12m: number
  avgMonthlyOutbound: number
  daysOnHand: number | null
  status: 'green' | 'yellow' | 'red' | 'no_movement'
  lastMovementDate: string | null
  refreshedAt: string
}

interface InventoryTurnoverSummary {
  total: number
  green: number
  yellow: number
  red: number
  no_movement: number
  refreshedAt: string | null
}

interface InventoryTurnoverResponse {
  rows: InventoryTurnoverRow[]
  total: number
  summary: InventoryTurnoverSummary
}

// ─── Status mapping ───────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  green: 'OK',
  yellow: 'Langsam',
  red: 'Kritisch',
  no_movement: 'Kein Abgang',
}

const STATUS_TO_BADGE: Record<string, string> = {
  green: 'success',
  yellow: 'warning',
  red: 'danger',
  no_movement: 'neutral',
}

function formatDays(days: number | null): string {
  if (days === null) return '—'
  if (days > 999) return '>999 Tage'
  return `${Math.round(days)} Tage`
}

// ─── Columns ─────────────────────────────────────────────────────────────────

const columns: ColumnDef<InventoryTurnoverRow>[] = [
  {
    accessorKey: 'itemCode',
    header: 'Artikelnr.',
    size: 120,
    cell: ({ getValue }) => (
      <span className="font-mono text-xs text-text-secondary">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'itemName',
    header: 'Bezeichnung',
    cell: ({ getValue }) => (
      <span className="text-sm font-medium text-text-primary line-clamp-2">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'groupName',
    header: 'Gruppe',
    size: 150,
    cell: ({ getValue }) => (
      <span className="text-sm text-text-secondary">{(getValue() as string | null) ?? '—'}</span>
    ),
  },
  {
    accessorKey: 'stockQuantity',
    header: 'Bestand',
    size: 90,
    cell: ({ getValue }) => (
      <span className="text-sm tabular-nums">{getValue() as number}</span>
    ),
  },
  {
    accessorKey: 'totalOutbound12m',
    header: 'Abgang 12M',
    size: 110,
    cell: ({ getValue }) => (
      <span className="text-sm tabular-nums">{getValue() as number}</span>
    ),
  },
  {
    accessorKey: 'daysOnHand',
    header: 'Reichweite',
    size: 110,
    cell: ({ getValue }) => (
      <span className="text-sm tabular-nums font-medium">{formatDays(getValue() as number | null)}</span>
    ),
  },
  {
    accessorKey: 'lastMovementDate',
    header: 'Letzter Abgang',
    size: 130,
    cell: ({ getValue }) => {
      const v = getValue() as string | null
      return <span className="text-sm text-text-secondary">{v ? new Date(v).toLocaleDateString('de-DE') : '—'}</span>
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cell: ({ getValue }) => {
      const s = getValue() as string
      return <StatusBadge status={s} variant={(STATUS_TO_BADGE[s] ?? 'neutral') as 'success' | 'warning' | 'danger' | 'neutral'} label={STATUS_LABELS[s] ?? s} />
    },
  },
]

// ─── Summary Cards ────────────────────────────────────────────────────────────

interface SummaryCardProps {
  icon: React.ReactNode
  label: string
  count: number
  badgeStatus: string
  active: boolean
  onClick: () => void
}

const CARD_COLORS: Record<string, { bg: string; border: string; activeBorder: string; icon: string }> = {
  danger:  { bg: 'bg-red-50',   border: 'border-red-200',   activeBorder: 'border-red-500',   icon: 'text-red-500' },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', activeBorder: 'border-amber-500', icon: 'text-amber-500' },
  success: { bg: 'bg-green-50', border: 'border-green-200', activeBorder: 'border-green-500', icon: 'text-green-500' },
  neutral: { bg: 'bg-gray-50',  border: 'border-gray-200',  activeBorder: 'border-gray-500',  icon: 'text-gray-400' },
}

function SummaryCard({ icon, label, count, badgeStatus, active, onClick }: SummaryCardProps) {
  const c = CARD_COLORS[badgeStatus] ?? CARD_COLORS.neutral
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${c.bg} ${active ? c.activeBorder : c.border}`}
    >
      <span className={c.icon}>{icon}</span>
      <div>
        <p className="text-xs text-text-secondary">{label}</p>
        <p className="text-lg font-semibold tabular-nums text-text-primary">{count}</p>
      </div>
    </button>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function InventoryTurnoverSection() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [search] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 50

  const { data, isLoading, error } = useQuery<InventoryTurnoverResponse>({
    queryKey: ['inventory-turnover', statusFilter, search, page],
    queryFn: () =>
      apiClient
        .get('/articles/inventory-turnover', {
          params: {
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: search || undefined,
            page,
            pageSize,
          },
        })
        .then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  })

  const { mutate: refresh, isPending: refreshing } = useMutation({
    mutationFn: () => apiClient.post('/articles/inventory-turnover/refresh', {}),
    onSuccess: (res) => {
      const d = res.data as { processed: number; totalInDb: number; tenantsOk: string[]; tenantsFailed: Array<{ code: string; error: string }> }
      const tenantInfo = d.tenantsOk.length > 0
        ? ` · SAP: ${d.tenantsOk.join(', ')}`
        : d.tenantsFailed.length > 0 ? ' · SAP nicht verfügbar' : ' · Keine SAP-Mandanten konfiguriert'
      const failInfo = d.tenantsFailed.length > 0
        ? ` · Fehler: ${d.tenantsFailed.map((t) => `${t.code}: ${t.error}`).join(' | ')}`
        : ''
      toast({
        title: 'Lagerumschlag aktualisiert',
        description: `${d.processed} Artikel verarbeitet${tenantInfo}${failInfo}`,
        tone: d.tenantsFailed.length > 0 && d.tenantsOk.length === 0 ? 'warning' : 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['inventory-turnover'] })
    },
    onError: (err: Error) => {
      toast({ title: 'Fehler beim Aktualisieren', description: err.message, tone: 'danger' })
    },
  })

  const summary = data?.summary

  const statusOptions: Array<{ key: string; label: string; count: number; icon: React.ReactNode; badge: string }> = [
    { key: 'red', label: 'Kritisch (>180 Tage)', count: summary?.red ?? 0, icon: <AlertTriangle size={16} />, badge: 'danger' },
    { key: 'yellow', label: 'Langsam (90–180 Tage)', count: summary?.yellow ?? 0, icon: <TrendingDown size={16} />, badge: 'warning' },
    { key: 'no_movement', label: 'Kein Abgang', count: summary?.no_movement ?? 0, icon: <PackageSearch size={16} />, badge: 'neutral' },
    { key: 'green', label: 'OK (<90 Tage)', count: summary?.green ?? 0, icon: <Clock size={16} />, badge: 'success' },
  ]

  if (isLoading) return <LoadingState label="Lagerumschlag wird geladen…" />
  if (error) return <ErrorState title="Fehler" description={(error as Error).message} />

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lagerumschlag"
        description={
          summary?.refreshedAt
            ? `Zuletzt aktualisiert: ${new Date(summary.refreshedAt).toLocaleString('de-DE')}`
            : 'Noch kein Refresh durchgeführt'
        }
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refresh()}
            disabled={refreshing}
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Wird berechnet…' : 'Jetzt aktualisieren'}
          </Button>
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {statusOptions.map((opt) => (
          <SummaryCard
            key={opt.key}
            icon={opt.icon}
            label={opt.label}
            count={opt.count}
            badgeStatus={opt.badge}
            active={statusFilter === opt.key}
            onClick={() => {
              setStatusFilter(statusFilter === opt.key ? 'all' : opt.key)
              setPage(1)
            }}
          />
        ))}
      </div>

      {/* Filter Bar */}
      <FilterBar>
        {statusFilter !== 'all' && (
          <FilterChip
            active
            onRemove={() => setStatusFilter('all')}
          >
            {statusOptions.find((o) => o.key === statusFilter)?.label ?? statusFilter}
          </FilterChip>
        )}
      </FilterBar>

      {/* Table */}
      {data?.rows.length === 0 ? (
        <EmptyState
          title="Keine Artikel gefunden"
          description={
            summary?.total === 0
              ? 'Noch kein Lagerumschlag berechnet. Klicke auf „Jetzt aktualisieren".'
              : 'Keine Artikel entsprechen den gewählten Filtern.'
          }
        />
      ) : (
        <DataTable
          data={data?.rows ?? []}
          columns={columns}
          pagination={{ pageSize }}
        />
      )}
    </div>
  )
}
